import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  alpha,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Warning,
  TrendingDown,
  TrendingUp,
  CheckCircle,
Cancel,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { riskAPI } from '../../services/api';

const RiskManagementWidget = () => {
  const navigate = useNavigate();
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRiskSignals();
  }, []);

  const fetchRiskSignals = async () => {
    try {
      const response = await riskAPI.getDailySignals();
      setSignals(response.data);
    } catch (error) {
      console.error('Failed to fetch risk signals:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!signals || signals.error) {
    return null; // Don't show if no data
  }

  const getExposureColor = (exposure) => {
    if (exposure >= 0.7) return '#4caf50';
    if (exposure >= 0.4) return '#ff9800';
    return '#f44336';
  };

  const getDrawdownColor = (dd) => {
    if (dd >= -0.04) return '#4caf50';
    if (dd >= -0.06) return '#ff9800';
    if (dd >= -0.08) return '#ff5722';
    return '#f44336';
  };

  const getCircuitBreakerLevel = (dd) => {
    if (dd > -0.04) return { level: 0, label: 'Normal', color: '#4caf50' };
    if (dd > -0.06) return { level: 1, label: 'Warning', color: '#ff9800' };
    if (dd > -0.08) return { level: 2, label: 'Critical', color: '#ff5722' };
    return { level: 3, label: 'Emergency', color: '#f44336' };
  };

  const cbStatus = getCircuitBreakerLevel(signals.dd || 0);
  const exposureColor = getExposureColor(signals.exposure || 0);
  const ddColor = getDrawdownColor(signals.dd || 0);

  return (
    <Card
      elevation={2}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => navigate('/risk-management')}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Risk Management
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Exposure & Drawdown Control
            </Typography>
          </Box>
          <Tooltip title="Xem chi tiết">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate('/risk-management'); }}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
          {/* Exposure */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(exposureColor, 0.1),
              border: '1px solid',
              borderColor: alpha(exposureColor, 0.3),
              textAlign: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
              <TrendingUp sx={{ fontSize: 18, color: exposureColor, mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Exposure
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: exposureColor }}>
              {((signals.exposure || 0) * 100).toFixed(0)}%
            </Typography>
          </Box>

          {/* Drawdown */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(ddColor, 0.1),
              border: '1px solid',
              borderColor: alpha(ddColor, 0.3),
              textAlign: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
              <TrendingDown sx={{ fontSize: 18, color: ddColor, mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Drawdown
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: ddColor }}>
              {((signals.dd || 0) * 100).toFixed(1)}%
            </Typography>
          </Box>

          {/* Circuit Breaker */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(cbStatus.color, 0.1),
              border: '1px solid',
              borderColor: alpha(cbStatus.color, 0.3),
              textAlign: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
              <Warning sx={{ fontSize: 18, color: cbStatus.color, mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                CB Level
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: cbStatus.color }}>
              {cbStatus.level}
            </Typography>
          </Box>
        </Box>

        {/* Entry Status */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={signals.can_enter ? <CheckCircle /> : <Cancel />}
            label={signals.can_enter ? 'Có thể vào lệnh' : 'Không vào lệnh'}
            color={signals.can_enter ? 'success' : 'error'}
            size="small"
          />
          <Chip
            label={`Regime: ${signals.regime || 'N/A'}`}
            color={signals.regime === 'RISK_ON' ? 'success' : 'error'}
            size="small"
          />
          {signals.no_trade && (
            <Chip
              icon={<Warning />}
              label="No-Trade Zone"
              color="warning"
              size="small"
            />
          )}
        </Box>

        {/* Circuit Breaker Alert */}
        {cbStatus.level >= 1 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha(cbStatus.color, 0.1),
              border: '1px solid',
              borderColor: cbStatus.color,
            }}
          >
            <Typography variant="caption" sx={{ color: cbStatus.color, fontWeight: 600 }}>
              ⚠️ Circuit Breaker {cbStatus.label}: Exposure cap = {((signals.cap || 0) * 100).toFixed(0)}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskManagementWidget;

